import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, CircularProgress, Alert, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import DataTable from '../common/DataTable';
import CompactFilterPanel from '../common/CompactFilterPanel';
import ImportDialog from '../common/ImportDialog';
import { Person, PersonTableColumn, PeopleResponse, FilterCriteria } from '../../types/people';
import { ApiService } from '../../services/api';
import { getErrorSeverity } from '../../utils/errorUtils';
import { useEditMode } from '../../hooks/useEditMode';
import { EditModeConfig } from '../../types/editMode';
import {
  EditModeControls,
  DeleteConfirmationDialog,
  RefreshNotificationDialog,
  EditWarningDialog,
} from '../common/editMode';
import { useEditWarning } from '../../hooks/useEditWarning';
import { usePermissions } from '../../hooks/usePermissions';
import { useImport } from '../../context/ImportContext';

const PeopleView: React.FC = () => {
  const { canEdit, canDownload } = usePermissions();
  const [showDownloadConfirmation, setShowDownloadConfirmation] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const importManager = useImport();

  // Auto-minimize import if user navigates away while import dialog is open
  useEffect(() => {
    return () => {
      // Cleanup: if import is running and dialog is open (not minimized), minimize it
      if (showImportDialog && 
          importManager.state.progress && 
          !importManager.state.isMinimized &&
          (importManager.state.progress.status === 'processing' || 
           importManager.state.progress.status === 'queued')) {
        importManager.minimize();
      }
    };
  }, [showImportDialog, importManager]);
  
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 100,
    total: 0
  });
  const [appliedFilters, setAppliedFilters] = useState<FilterCriteria[]>([]);
  const [sortConfig, setSortConfig] = useState<{ column: string; direction: 'ASC' | 'DESC' }>({ column: 'id', direction: 'ASC' });

  // Define all available columns - including ID columns for edit mode
  // Memoize allColumns to prevent unnecessary re-renders in useMemo dependencies
  // Ordered according to user specification
  const allColumns: PersonTableColumn[] = useMemo(() => [
    // First three columns are sticky
    { id: 'first_name', label: 'First Name', minWidth: 120, type: 'text' },
    { id: 'last_name', label: 'Last Name', minWidth: 120, type: 'text' },
    { id: 'email', label: 'Email', minWidth: 200, type: 'text' },
    // Ordered columns
    { id: 'company_name', label: 'Company Name', minWidth: 150, type: 'text' },
    { id: 'title', label: 'Title', minWidth: 150, type: 'text' },
    { id: 'notes', label: 'Notes', minWidth: 200, type: 'text' },
    { id: 'Description', label: 'Description', minWidth: 300, type: 'text' },
    { id: 'unsubscribe', label: 'Unsubscribe', minWidth: 120, type: 'boolean' },
    { id: 'in_mailer', label: 'In Mailer', minWidth: 120, type: 'boolean' },
    { id: 'state_name', label: 'State', minWidth: 100, type: 'text' },
    { id: 'location', label: 'Location', minWidth: 150, type: 'text' },
    { id: 'California_location', label: 'California Location', minWidth: 200, type: 'text' },
    { id: 'country_name', label: 'Country', minWidth: 100, type: 'text' },
    { id: 'area', label: 'Area', minWidth: 150, type: 'text' },
    { id: 'date_added', label: 'Date Added', minWidth: 120, type: 'date' },
    { id: 'last_emailed', label: 'Last Emailed', minWidth: 120, type: 'date' },
    { id: 'industry_name', label: 'Industry Tag', minWidth: 150, type: 'text' },
    { id: 'industry_tag_two_name', label: 'Industry Tag Two', minWidth: 150, type: 'text' },
    { id: 'industry_tag_three_name', label: 'Industry Tag Three', minWidth: 150, type: 'text' },
    { id: 'industry_tag_four_name', label: 'Industry Tag Four', minWidth: 150, type: 'text' },
    { id: 'verification_status_name', label: 'Verification Status', minWidth: 150, type: 'text' },
    { id: 'verification_date', label: 'Verification Date', minWidth: 150, type: 'text' },
    { id: 'linkedin_id', label: 'LinkedIn ID', minWidth: 150, type: 'number' },
    { id: 'linkedin', label: 'LI', minWidth: 60, type: 'text' },
    { id: 'linkedin_group_id', label: 'LinkedIn Group ID', minWidth: 150, type: 'number' },
    { id: 'twitter', label: 'TW', minWidth: 60, type: 'text' },
    { id: 'facebook', label: 'FB', minWidth: 60, type: 'text' },
    { id: 'ig', label: 'IG', minWidth: 60, type: 'text' },
    { id: 'brand_follower', label: 'Brand Follower', minWidth: 140, type: 'text' },
    { id: 'username', label: 'Username', minWidth: 120, type: 'text' },
    { id: 'p_tag_1_name', label: 'P Tag 1', minWidth: 120, type: 'text' },
    { id: 'p_tag_2_name', label: 'P Tag 2', minWidth: 120, type: 'text' },
    { id: 'p_tag_3_name', label: 'P Tag 3', minWidth: 120, type: 'text' },
    { id: 'p_tag_4_name', label: 'P Tag 4', minWidth: 120, type: 'text' },
    { 
      id: 'type_', 
      label: 'Type', 
      minWidth: 100, 
      type: 'autocomplete',
      options: [
        'THC',
        'CBD',
        'INV',
        'MED',
        'THC, CBD',
        'THC, INV',
        'THC, MED',
        'CBD, INV',
        'CBD, MED',
        'INV, MED',
        'THC, CBD, INV',
        'THC, CBD, MED',
        'CBD, INV, MED',
        'THC, CBD, INV, MED'
      ]
    },
    { id: 'company_type', label: 'Company Type', minWidth: 140, type: 'text' },
    { id: 'license', label: 'License', minWidth: 120, type: 'text' },
    { id: 'created_at', label: 'Created At', minWidth: 150, type: 'text' },
    { id: 'date_add_in_mailer', label: 'Date Add in Mailer', minWidth: 150, type: 'text' },
    { id: 'date_updated', label: 'Date Updated', minWidth: 120, type: 'date' },
    { id: 'last_clicked', label: 'Last Clicked', minWidth: 120, type: 'date' },
    { id: 'last_opened', label: 'Last Opened', minWidth: 120, type: 'date' },
    { id: 'news_thc', label: 'News THC', minWidth: 120, type: 'boolean' },
    { id: 'news_cbd', label: 'News CBD', minWidth: 120, type: 'boolean' },
    { id: 'news_inv', label: 'News INV', minWidth: 120, type: 'boolean' },
    { id: 'news_med', label: 'News MED', minWidth: 120, type: 'boolean' },
    // ID columns (shown in edit mode, hidden in view mode)
    { id: 'state_usa', label: 'State', minWidth: 100, type: 'state' },
    { id: 'country', label: 'Country', minWidth: 100, type: 'country' },
    { id: 'industry_tag', label: 'Industry Tag', minWidth: 150, type: 'industryTag' },
    { id: 'industry_tag_two', label: 'Industry Tag Two', minWidth: 150, type: 'industryTag' },
    { id: 'industry_tag_three', label: 'Industry Tag Three', minWidth: 150, type: 'industryTag' },
    { id: 'industry_tag_four', label: 'Industry Tag Four', minWidth: 150, type: 'industryTag' },
    { id: 'verification_status', label: 'Verification Status ID', minWidth: 150, type: 'number' },
    { id: 'p_tag_1', label: 'P Tag 1', minWidth: 120, type: 'industryTag' },
    { id: 'p_tag_2', label: 'P Tag 2', minWidth: 120, type: 'industryTag' },
    { id: 'p_tag_3', label: 'P Tag 3', minWidth: 120, type: 'industryTag' },
    { id: 'p_tag_4', label: 'P Tag 4', minWidth: 120, type: 'industryTag' },
    { id: 'id_company', label: 'Company ID', minWidth: 120, type: 'number' },
    // Additional columns not in user's list (added to end)
    { id: 'oficine_phone', label: 'Office Phone', minWidth: 150, type: 'text' },
    { id: 'status', label: 'Status', minWidth: 100, type: 'number' },
    { id: 'leadleaper_id', label: 'Leadleaper ID', minWidth: 150, type: 'text' },
    { id: 'b2b_or_b2c', label: 'B2B/B2C', minWidth: 100, type: 'text' },
    { id: 'bounce', label: 'Bounce', minWidth: 100, type: 'boolean' },
    // ID column must be last
    { id: 'id', label: 'ID', minWidth: 80, type: 'number' },
  ], []); // Empty dependency array since columns are static

  // ID columns that should be unchecked by default in view mode
  const idColumns = ['state_usa', 'country', 'industry_tag', 
    'industry_tag_two', 'industry_tag_three', 'industry_tag_four',
    'p_tag_1', 'p_tag_2', 'p_tag_3', 'p_tag_4', 'id_company', 'verification_status'];

  const [visibleColumns, setVisibleColumns] = useState(
    allColumns.map(col => ({ 
      id: col.id, 
      label: col.label, 
      visible: idColumns.includes(col.id) ? false : true 
    }))
  );

  // Load people data - memoized to prevent infinite loops
  const loadPeople = useCallback(async (page: number = 0, filters: FilterCriteria[] = [], refresh?: boolean) => {
    setLoading(true);
    setError(null);
    try {
      let response: PeopleResponse;
      
      if (filters.length > 0) {
        response = await ApiService.searchPeople({
          filters,
          page,
          limit: 100,
          sort: sortConfig.column,
          direction: sortConfig.direction,
          refresh
        });
      } else {
        response = await ApiService.getPeople({
          page,
          limit: 100,
          sort: sortConfig.column,
          direction: sortConfig.direction,
          refresh
        });
      }

      // Always replace data (no appending in page-based pagination)
      setPeople(response.data);
      
      setPagination({
        page: page,
        limit: 100,
        total: response.pagination.total
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load people');
      console.error('Error loading people:', err);
    } finally {
      setLoading(false);
    }
  }, [sortConfig.column, sortConfig.direction]);

  // Load initial data - only on mount
  useEffect(() => {
    loadPeople(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    loadPeople(newPage, appliedFilters);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    setPagination({ page: 0, limit: newLimit, total: pagination.total });
    // For now, we keep limit at 100. If you want to support changing it, add backend support
    loadPeople(0, appliedFilters);
  };

  // Handle filters apply
  const handleFiltersApply = (newFilters: FilterCriteria[]) => {
    setAppliedFilters(newFilters);
    // Reset to page 0 with new filters
    setPagination(prev => ({ ...prev, page: 0 }));
    loadPeople(0, newFilters);
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setAppliedFilters([]);
    // Reset to page 0
    setPagination(prev => ({ ...prev, page: 0 }));
    loadPeople(0, []);
  };

  // Handle sort
  const handleSort = useCallback((column: string) => {
    // Map frontend column to backend sort column
    let backendSortColumn = column;
    if (column === 'industry_name') {
      backendSortColumn = 'industry_tag';
    } else if (column === 'state_name') {
      backendSortColumn = 'state_usa';
    } else if (column === 'country_name') {
      backendSortColumn = 'country';
    } else if (column === 'p_tag_1_name') {
      backendSortColumn = 'p_tag_1';
    } else if (column === 'p_tag_2_name') {
      backendSortColumn = 'p_tag_2';
    } else if (column === 'p_tag_3_name') {
      backendSortColumn = 'p_tag_3';
    } else if (column === 'p_tag_4_name') {
      backendSortColumn = 'p_tag_4';
    } else if (column === 'industry_tag_two_name') {
      backendSortColumn = 'industry_tag_two';
    } else if (column === 'industry_tag_three_name') {
      backendSortColumn = 'industry_tag_three';
    } else if (column === 'industry_tag_four_name') {
      backendSortColumn = 'industry_tag_four';
    } else if (column === 'verification_status_name') {
      backendSortColumn = 'verification_status';
    }
    
    // Determine new sort direction
    setSortConfig(prev => {
      const newDirection = prev.column === backendSortColumn && prev.direction === 'ASC' ? 'DESC' : 'ASC';
      return {
        column: backendSortColumn,
        direction: newDirection
      };
    });
    
    // Reset to page 0 with new sort
    setPagination(prev => ({ ...prev, page: 0 }));
    
    // Reload data with new sort
    setTimeout(() => {
      loadPeople(0, appliedFilters);
    }, 0);
  }, [loadPeople, appliedFilters]);

  // Handle column toggle
  const handleColumnToggle = (columnId: string, visible: boolean) => {
    setVisibleColumns(prev => 
      prev.map(col => 
        col.id === columnId ? { ...col, visible } : col
      )
    );
  };

  // Edit Mode Configuration
  const editModeConfig: EditModeConfig<Person> = {
    apiMethods: {
      create: (data: any) => ApiService.createPerson(data),
      update: (id: number, data: any) => ApiService.updatePerson(id, data),
      delete: (id: number) => ApiService.deletePerson(id),
    },
    fields: {
      id: { type: 'number', required: false, editable: false },
      first_name: { type: 'text', required: false, editable: true, defaultValue: undefined },
      last_name: { type: 'text', required: false, editable: true, defaultValue: undefined },
      email: { type: 'text', required: false, editable: true, defaultValue: undefined },
      verification_status: { type: 'number', required: false, editable: true, defaultValue: undefined },
      oficine_phone: { type: 'text', required: false, editable: true, defaultValue: undefined },
      location: { type: 'text', required: false, editable: true, defaultValue: undefined },
      id_company: { type: 'number', required: false, editable: true, defaultValue: undefined },
      title: { type: 'text', required: false, editable: true, defaultValue: undefined },
      linkedin: { type: 'text', required: false, editable: true, defaultValue: undefined },
      status: { type: 'number', required: false, editable: true, defaultValue: 1 },
      country: { type: 'number', required: false, editable: true, defaultValue: undefined },
      state_usa: { type: 'number', required: false, editable: true, defaultValue: undefined },
      area: { type: 'text', required: false, editable: true, defaultValue: undefined },
      leadleaper_id: { type: 'text', required: false, editable: true, defaultValue: undefined },
      linkedin_group_id: { type: 'number', required: false, editable: true, defaultValue: 0 },
      verification_date: { type: 'text', required: false, editable: true, defaultValue: undefined },
      unsubscribe: { type: 'number', required: false, editable: true, defaultValue: 0 },
      industry_tag: { type: 'number', required: false, editable: true, defaultValue: undefined },
      twitter: { type: 'text', required: false, editable: true, defaultValue: undefined },
      facebook: { type: 'text', required: false, editable: true, defaultValue: undefined },
      ig: { type: 'text', required: false, editable: true, defaultValue: undefined },
      notes: { type: 'text', required: false, editable: true, defaultValue: undefined },
      in_mailer: { type: 'number', required: false, editable: true, defaultValue: 0 },
      date_add_in_mailer: { type: 'text', required: false, editable: true, defaultValue: undefined },
      industry_tag_two: { type: 'number', required: false, editable: true, defaultValue: undefined },
      industry_tag_three: { type: 'number', required: false, editable: true, defaultValue: undefined },
      industry_tag_four: { type: 'number', required: false, editable: true, defaultValue: undefined },
      brand_follower: { type: 'text', required: false, editable: true, defaultValue: undefined },
      username: { type: 'text', required: false, editable: true, defaultValue: undefined },
      b2b_or_b2c: { type: 'text', required: false, editable: true, defaultValue: undefined },
      type_: { type: 'text', required: false, editable: true, defaultValue: undefined },
      license: { type: 'text', required: false, editable: true, defaultValue: undefined },
      California_location: { type: 'text', required: false, editable: true, defaultValue: undefined },
      company_type: { type: 'text', required: false, editable: true, defaultValue: undefined },
      Description: { type: 'text', required: false, editable: true, defaultValue: undefined },
      p_tag_1: { type: 'number', required: false, editable: true, defaultValue: undefined },
      p_tag_2: { type: 'number', required: false, editable: true, defaultValue: undefined },
      p_tag_3: { type: 'number', required: false, editable: true, defaultValue: undefined },
      p_tag_4: { type: 'number', required: false, editable: true, defaultValue: undefined },
      date_added: { type: 'text', required: false, editable: true, defaultValue: undefined },
      date_updated: { type: 'text', required: false, editable: true, defaultValue: undefined },
      last_emailed: { type: 'text', required: false, editable: true, defaultValue: undefined },
      last_clicked: { type: 'text', required: false, editable: true, defaultValue: undefined },
      last_opened: { type: 'text', required: false, editable: true, defaultValue: undefined },
      bounce: { type: 'number', required: false, editable: true, defaultValue: 0 },
      linkedin_id: { type: 'number', required: false, editable: true, defaultValue: undefined },
      news_thc: { type: 'number', required: false, editable: true, defaultValue: 0 },
      news_cbd: { type: 'number', required: false, editable: true, defaultValue: 0 },
      news_inv: { type: 'number', required: false, editable: true, defaultValue: 0 },
      news_med: { type: 'number', required: false, editable: true, defaultValue: 0 },
      // Read-only joined fields
      company_name: { type: 'text', required: false, editable: false, defaultValue: undefined },
      country_name: { type: 'text', required: false, editable: false, defaultValue: undefined },
      state_name: { type: 'text', required: false, editable: false, defaultValue: undefined },
      industry_name: { type: 'text', required: false, editable: false, defaultValue: undefined },
      industry_tag_two_name: { type: 'text', required: false, editable: false, defaultValue: undefined },
      industry_tag_three_name: { type: 'text', required: false, editable: false, defaultValue: undefined },
      industry_tag_four_name: { type: 'text', required: false, editable: false, defaultValue: undefined },
      p_tag_1_name: { type: 'text', required: false, editable: false, defaultValue: undefined },
      p_tag_2_name: { type: 'text', required: false, editable: false, defaultValue: undefined },
      p_tag_3_name: { type: 'text', required: false, editable: false, defaultValue: undefined },
      p_tag_4_name: { type: 'text', required: false, editable: false, defaultValue: undefined },
      verification_status_name: { type: 'text', required: false, editable: false, defaultValue: undefined },
      created_at: { type: 'text', required: false, editable: false, defaultValue: undefined },
    },
    primaryKey: 'id',
    displayName: 'Person',
    validate: (row, isNew) => {
      // No required fields for people
      return null;
    },
    prepareData: (row, originalRow) => {
      // Helper to convert null to undefined for Person type compatibility
      const toUndefined = (value: any) => (value === null ? undefined : value);
      
      // For new rows, ensure all fields are properly set
      if (!originalRow) {
        return {
          first_name: toUndefined(row.first_name),
          last_name: toUndefined(row.last_name),
          email: toUndefined(row.email),
          verification_status: toUndefined(row.verification_status),
          oficine_phone: toUndefined(row.oficine_phone),
          location: toUndefined(row.location),
          id_company: toUndefined(row.id_company),
          title: toUndefined(row.title),
          linkedin: toUndefined(row.linkedin),
          status: row.status !== undefined ? (typeof row.status === 'number' ? row.status : parseInt(String(row.status)) || 1) : 1,
          country: toUndefined(row.country),
          state_usa: toUndefined(row.state_usa),
          area: toUndefined(row.area),
          leadleaper_id: toUndefined(row.leadleaper_id),
          linkedin_group_id: row.linkedin_group_id !== undefined ? (typeof row.linkedin_group_id === 'number' ? row.linkedin_group_id : parseInt(String(row.linkedin_group_id)) || 0) : 0,
          verification_date: toUndefined(row.verification_date),
          unsubscribe: row.unsubscribe !== undefined ? (typeof row.unsubscribe === 'number' ? row.unsubscribe : parseInt(String(row.unsubscribe)) || 0) : 0,
          industry_tag: toUndefined(row.industry_tag),
          twitter: toUndefined(row.twitter),
          facebook: toUndefined(row.facebook),
          ig: toUndefined(row.ig),
          notes: toUndefined(row.notes),
          in_mailer: row.in_mailer !== undefined ? (typeof row.in_mailer === 'number' ? row.in_mailer : parseInt(String(row.in_mailer)) || 0) : 0,
          date_add_in_mailer: toUndefined(row.date_add_in_mailer),
          industry_tag_two: toUndefined(row.industry_tag_two),
          industry_tag_three: toUndefined(row.industry_tag_three),
          industry_tag_four: toUndefined(row.industry_tag_four),
          brand_follower: toUndefined(row.brand_follower),
          username: toUndefined(row.username),
          b2b_or_b2c: toUndefined(row.b2b_or_b2c),
          type_: toUndefined(row.type_),
          license: toUndefined(row.license),
          California_location: toUndefined(row.California_location),
          company_type: toUndefined(row.company_type),
          Description: toUndefined(row.Description),
          p_tag_1: toUndefined(row.p_tag_1),
          p_tag_2: toUndefined(row.p_tag_2),
          p_tag_3: toUndefined(row.p_tag_3),
          p_tag_4: toUndefined(row.p_tag_4),
          date_added: toUndefined(row.date_added),
          date_updated: toUndefined(row.date_updated),
          last_emailed: toUndefined(row.last_emailed),
          last_clicked: toUndefined(row.last_clicked),
          last_opened: toUndefined(row.last_opened),
          bounce: row.bounce !== undefined ? (typeof row.bounce === 'number' ? row.bounce : parseInt(String(row.bounce)) || 0) : 0,
          linkedin_id: toUndefined(row.linkedin_id),
          news_thc: row.news_thc !== undefined ? (typeof row.news_thc === 'number' ? row.news_thc : parseInt(String(row.news_thc)) || 0) : 0,
          news_cbd: row.news_cbd !== undefined ? (typeof row.news_cbd === 'number' ? row.news_cbd : parseInt(String(row.news_cbd)) || 0) : 0,
          news_inv: row.news_inv !== undefined ? (typeof row.news_inv === 'number' ? row.news_inv : parseInt(String(row.news_inv)) || 0) : 0,
          news_med: row.news_med !== undefined ? (typeof row.news_med === 'number' ? row.news_med : parseInt(String(row.news_med)) || 0) : 0,
        };
      }
      
      // OPTIMIZATION: For updates, ONLY process fields that are in the row (changed fields)
      const updateData: Partial<Person> = {};
      
      // Only process fields that actually changed (exist in row)
      Object.keys(row).forEach(key => {
        const fieldKey = key as keyof Person;
        
        // Handle special cases
        if (fieldKey === 'status') {
          updateData.status = typeof row.status === 'number' 
            ? row.status 
            : parseInt(String(row.status)) || originalRow?.status || 1;
        } else if (fieldKey === 'unsubscribe') {
          updateData.unsubscribe = typeof row.unsubscribe === 'number'
            ? row.unsubscribe
            : parseInt(String(row.unsubscribe)) || originalRow?.unsubscribe || 0;
        } else if (fieldKey === 'in_mailer') {
          updateData.in_mailer = typeof row.in_mailer === 'number'
            ? row.in_mailer
            : parseInt(String(row.in_mailer)) || originalRow?.in_mailer || 0;
        } else if (fieldKey === 'bounce') {
          updateData.bounce = typeof row.bounce === 'number'
            ? row.bounce
            : parseInt(String(row.bounce)) || originalRow?.bounce || 0;
        } else if (fieldKey === 'linkedin_group_id') {
          updateData.linkedin_group_id = typeof row.linkedin_group_id === 'number'
            ? row.linkedin_group_id
            : parseInt(String(row.linkedin_group_id)) || originalRow?.linkedin_group_id || 0;
        } else if (['news_thc', 'news_cbd', 'news_inv', 'news_med'].includes(fieldKey)) {
          (updateData as any)[fieldKey] = typeof (row as any)[fieldKey] === 'number'
            ? (row as any)[fieldKey]
            : parseInt(String((row as any)[fieldKey])) || (originalRow as any)?.[fieldKey] || 0;
        } else {
          // For all other fields, always include the value (including null)
          // This ensures that clearing a field (setting to null) is properly saved
          const value = (row as any)[fieldKey];
          // Always include the value - null is a valid value that means "clear this field"
          (updateData as any)[fieldKey] = value;
        }
      });
      
      return updateData;
    },
  };

  // Use the edit mode hook
  const editMode = useEditMode(editModeConfig, {
    data: people,
    onDataChange: setPeople,
    onRefresh: async () => {
      await loadPeople(pagination.page, appliedFilters, true);
    },
    onError: (errorMessage) => {
      setError(errorMessage);
    },
  });

  // Filter columns based on visibility and edit mode
  // In edit mode: hide name columns, show ID columns (maintaining order)
  // In view mode: respect visibleColumns state (ID columns unchecked by default but can be shown if selected)
  const columns = useMemo(() => {
    // Map name columns to their corresponding ID columns
    const nameToIdMap: Record<string, string> = {
      'state_name': 'state_usa',
      'country_name': 'country',
      'industry_name': 'industry_tag',
      'industry_tag_two_name': 'industry_tag_two',
      'industry_tag_three_name': 'industry_tag_three',
      'industry_tag_four_name': 'industry_tag_four',
      'p_tag_1_name': 'p_tag_1',
      'p_tag_2_name': 'p_tag_2',
      'p_tag_3_name': 'p_tag_3',
      'p_tag_4_name': 'p_tag_4',
      'company_name': 'id_company',
      'verification_status_name': 'verification_status',
    };
    
    // Build filtered columns maintaining order from allColumns
    const filtered: typeof allColumns = [];
    
    allColumns.forEach(col => {
      const isVisible = visibleColumns.find(vc => vc.id === col.id)?.visible;
      
      if (editMode.isEditMode) {
        // In edit mode: replace name columns with ID columns, hide ID columns that have name equivalents
        if (nameToIdMap[col.id]) {
          // This is a name column - replace with corresponding ID column
          const idColumnId = nameToIdMap[col.id];
          const idColumn = allColumns.find(c => c.id === idColumnId);
          if (idColumn && !filtered.find(c => c.id === idColumnId)) {
            filtered.push(idColumn);
          }
        } else if (!Object.values(nameToIdMap).includes(col.id)) {
          // This is not an ID column that replaces a name column - include if visible
          if (isVisible) {
            filtered.push(col);
          }
        }
        // Skip ID columns that replace name columns (they're already added above)
      } else {
        // In non-edit mode: respect visibleColumns state
        if (isVisible) {
          filtered.push(col);
        }
      }
    });
    
    return filtered;
  }, [allColumns, visibleColumns, editMode.isEditMode]);

  // Use the edit warning hook
  const editWarning = useEditWarning('editWarningShown_global', () => {
    editMode.handleEdit();
  });

  const handleEdit = () => {
    if (editMode.isEditMode) {
      editMode.handleCancel();
      return;
    }
    editWarning.handleEditClick();
  };

  const handleImport = () => {
    setShowImportDialog(true);
  };

  const handleImportComplete = () => {
    // Reload people after import
    loadPeople(pagination.page, appliedFilters, true);
    setShowImportDialog(false);
  };

  const handleDownload = () => {
    // Show confirmation dialog first
    setShowDownloadConfirmation(true);
  };

  const handleConfirmDownload = async () => {
    setShowDownloadConfirmation(false);
    
    try {
      setLoading(true);
      setError(null);

      // Get filters from state
      const filtersToUse = appliedFilters.length > 0 ? appliedFilters : [];

      // Call export API
      const blob = await ApiService.exportPeople(filtersToUse);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `people_export_${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error('Error downloading people:', err);
      setError(err instanceof Error ? err.message : 'Failed to download people');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: '100%',
      display: 'flex', 
      flexDirection: 'column',
      overflowX: 'hidden'
    }}>
      {/* Header Section */}
      <Box sx={{ 
        flexShrink: 0,
        backgroundColor: 'white',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        mb: 0.5
      }}>
        
        {error && (
          <Alert severity={getErrorSeverity(error)} sx={{ m: 2, mt: 0 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <CompactFilterPanel
              columns={visibleColumns}
              onColumnToggle={handleColumnToggle}
              onFiltersApply={handleFiltersApply}
              onClearFilters={handleClearFilters}
              onEdit={canEdit ? handleEdit : undefined}
              onImport={handleImport}
              onDownload={canDownload ? handleDownload : undefined}
              onAddNew={editMode.handleAddNew}
              filterType="people"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading...
                </Typography>
              </Box>
            )}

            {/* Inline Top Pagination Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mr: 0.5 }}>
              <span style={{ fontSize: '0.8125rem', color: 'rgba(0, 0, 0, 0.6)', fontWeight: 500 }}>
                {pagination.page * 100 + 1}-{Math.min((pagination.page + 1) * 100, pagination.total)} of {pagination.total}
              </span>
              <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <button
                  onClick={(e) => handlePageChange(e, pagination.page - 1)}
                  disabled={pagination.page === 0}
                  style={{
                    padding: '2px 6px',
                    border: '1px solid rgba(0, 0, 0, 0.15)',
                    borderRadius: '4px',
                    backgroundColor: pagination.page === 0 ? 'rgba(0, 0, 0, 0.02)' : '#fff',
                    cursor: pagination.page === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '0.8125rem',
                    opacity: pagination.page === 0 ? 0.4 : 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '24px',
                    width: '24px',
                    boxShadow: 'none',
                    outline: 'none'
                  }}
                  title="Previous Page"
                >
                  ‹
                </button>
                <button
                  onClick={(e) => handlePageChange(e, pagination.page + 1)}
                  disabled={pagination.page >= Math.ceil(pagination.total / 100) - 1}
                  style={{
                    padding: '2px 6px',
                    border: '1px solid rgba(0, 0, 0, 0.15)',
                    borderRadius: '4px',
                    backgroundColor: pagination.page >= Math.ceil(pagination.total / 100) - 1 ? 'rgba(0, 0, 0, 0.02)' : '#fff',
                    cursor: pagination.page >= Math.ceil(pagination.total / 100) - 1 ? 'not-allowed' : 'pointer',
                    fontSize: '0.8125rem',
                    opacity: pagination.page >= Math.ceil(pagination.total / 100) - 1 ? 0.4 : 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '24px',
                    width: '24px',
                    boxShadow: 'none',
                    outline: 'none'
                  }}
                  title="Next Page"
                >
                  ›
                </button>
              </Box>
            </Box>

            <Button
              variant="outlined"
              onClick={() => {
                if (editMode.isEditMode) {
                  editMode.handleCancel();
                }
                loadPeople(pagination.page, appliedFilters, true);
              }}
              sx={{ 
                minWidth: '36px', 
                width: '36px', 
                height: '36px', 
                borderRadius: '50%', 
                p: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderColor: 'rgba(0, 0, 0, 0.15)',
                color: 'rgba(0, 0, 0, 0.6)',
                '&:hover': {
                  borderColor: 'rgba(0, 0, 0, 0.3)',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                }
              }}
            >
              <RefreshIcon sx={{ fontSize: '1.25rem' }} />
            </Button>
          </Box>
        </Box>
        
        {/* Edit Mode Controls - Visible for all users */}
        {canEdit && (
          <EditModeControls
            isEditMode={editMode.isEditMode}
            saving={editMode.saving}
            onSave={editMode.handleSave}
            onCancel={editMode.handleCancel}
            onAddNew={editMode.handleAddNew}
            selectedRowsRef={editMode.selectedRowsRef}
            onDeleteClick={editMode.handleDeleteClick}
            deleteDialogRef={editMode.deleteDialogRef}
            refreshNotificationRef={editMode.refreshNotificationRef}
          />
        )}
      </Box>
      
      {/* Table Section */}
      <Box sx={{ 
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        mb: 3
      }}>
        <Box sx={{ 
          width: '100%',
          maxWidth: '100%',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          overflow: 'hidden', // Clip content, scrolling handled by TableContainer
        }}>
          <DataTable
            columns={columns}
            rows={editMode.displayRows}
            page={pagination.page}
            rowsPerPage={100}
            totalRows={pagination.total}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            {...({ onSort: handleSort, sortConfig } as any)}
            loading={loading}
            isEditMode={editMode.isEditMode}
            onCellEdit={editMode.handleCellEdit}
            editableColumns={columns.filter(col => {
              const readOnlyFields = ['id', 'company_name', 'country_name', 'state_name', 
                'industry_name', 'industry_tag_two_name', 'industry_tag_three_name', 
                'industry_tag_four_name', 'p_tag_1_name', 'p_tag_2_name', 'p_tag_3_name', 
                'p_tag_4_name', 'created_at'];
              return !readOnlyFields.includes(col.id);
            }).map(col => col.id)}
            isRowSelected={editMode.isRowSelected}
            selectedRowsCount={editMode.selectedRowsCount}
            selectAllVersion={editMode.selectAllVersion}
            onRowSelect={editMode.handleRowSelect}
            onSelectAll={editMode.handleSelectAll}
            savingRows={new Set()}
            stickyColumns={['first_name', 'last_name', 'email']}
          />
        </Box>
      </Box>

      {/* Edit Warning Dialog */}
      <EditWarningDialog
        open={editWarning.showWarning}
        onClose={editWarning.handleCancel}
        onProceed={editWarning.handleProceed}
        title="Important"
        message="Please modify the database carefully and adhere to the data types of those columns. For reference, check old records before entering values. Currently there are no constraint checks, so please don't mess up the database."
      />

      {/* Download Confirmation Dialog */}
      <Dialog open={showDownloadConfirmation} onClose={() => setShowDownloadConfirmation(false)}>
        <DialogTitle>Confirm Download</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to download the people data as a CSV file. Please ensure you handle this data responsibly and do not misuse it. The download will include all people records based on your current filters.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDownloadConfirmation(false)} color="inherit">Cancel</Button>
          <Button onClick={handleConfirmDownload} variant="contained" color="primary">Confirm Download</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        dialogRef={editMode.deleteDialogRef}
        selectedRowsRef={editMode.selectedRowsRef}
        onConfirm={editMode.handleConfirmDelete}
      />

      {/* Refresh Notification Dialog */}
      <RefreshNotificationDialog
        dialogRef={editMode.refreshNotificationRef}
      />

      {/* Import Dialog - only show if not managed by global import manager */}
      {showImportDialog && !importManager.state.progress && (
        <ImportDialog
          open={showImportDialog}
          onClose={() => setShowImportDialog(false)}
          type="people"
          onImportComplete={handleImportComplete}
        />
      )}
    </Box>
  );
};

export default PeopleView;
